import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Settings, Plus } from 'lucide-react';
import api from '../services/api';
import { invalidatePlantillas } from '../services/cachedCatalogs';
import { useConfirm } from './ui';
import PlantillaCategoriasModal from './PlantillaCategoriasModal';

const PlantillasList = () => {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [plantillas, setPlantillas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCategoriasModal, setShowCategoriasModal] = useState(false);

    useEffect(() => {
        cargarPlantillas();
    }, []);

    const cargarPlantillas = async () => {
        try {
            const response = await api.plantillas.getAll();
            setPlantillas(response.data || []);
        } catch (err) {
            console.error('Error al cargar plantillas:', err);
            toast.error('Error al cargar plantillas');
        } finally {
            setLoading(false);
        }
    };

    const handleEliminar = async (id, nombre) => {
        const ok = await confirm({
            title: '¿Eliminar plantilla?',
            message: <p>Vas a eliminar la plantilla <strong>{nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.plantillas.delete(id);
            invalidatePlantillas();
            toast.success('Plantilla eliminada');
            cargarPlantillas();
        } catch (err) {
            toast.error('Error al eliminar');
        }
    };

    if (loading) {
        return <div className="loading">Cargando plantillas...</div>;
    }

    return (
        <>
        <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <h2>Plantillas de Cotización</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                        className="btn small btn-outline-primary"
                        onClick={() => setShowCategoriasModal(true)}
                    >
                        <Settings size={14} />
                        Gestionar Categorías
                    </button>
                    <button
                        className="btn small neon"
                        onClick={() => navigate('/plantillas/nueva')}
                    >
                        <Plus size={14} />
                        Nueva Plantilla
                    </button>
                </div>
            </div>

            <div className="card-body">
                {plantillas.length === 0 ? (
                    <div className="empty-state">
                        <p>No hay plantillas creadas</p>
                        <p style={{ color: 'var(--muted)', fontSize: '14px', marginTop: '8px' }}>
                            Las plantillas te permiten crear cotizaciones rápidamente con servicios predefinidos
                        </p>
                    </div>
                ) : (
                    // Agrupar por categoría: el backend ya devuelve los datos ordenados
                    // por categoria_orden, luego nombre. Recorremos en orden y abrimos
                    // un grupo nuevo cada vez que cambia la categoría.
                    (() => {
                        const grupos = [];
                        plantillas.forEach(p => {
                            const key = p.categoria_id || 'sin-categoria';
                            const ultimo = grupos[grupos.length - 1];
                            if (!ultimo || ultimo.key !== key) {
                                grupos.push({
                                    key,
                                    nombre: p.categoria_nombre || 'Sin categoría',
                                    items: [p]
                                });
                            } else {
                                ultimo.items.push(p);
                            }
                        });
                        return grupos.map(grupo => (
                            <div key={grupo.key} className="plantillas-grupo">
                                <div className="plantillas-grupo-header">
                                    <h3 className="plantillas-grupo-title">{grupo.nombre}</h3>
                                    <span className="plantillas-grupo-count">
                                        {grupo.items.length} {grupo.items.length === 1 ? 'plantilla' : 'plantillas'}
                                    </span>
                                </div>
                                <div className="plantillas-grid">
                                    {grupo.items.map(plantilla => (
                                        <div key={plantilla.id} className="plantilla-card">
                                            <div className="plantilla-card-header">
                                                <h3>{plantilla.nombre}</h3>
                                                <span className={`badge ${plantilla.activo ? 'active' : 'inactive'}`}>
                                                    {plantilla.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </div>
                                            {plantilla.descripcion && (
                                                <p className="plantilla-descripcion">{plantilla.descripcion}</p>
                                            )}
                                            <div className="plantilla-meta">
                                                <span>{plantilla.servicios_count || 0} servicios</span>
                                                <span>{plantilla.extras_count || 0} extras</span>
                                            </div>
                                            <div className="plantilla-actions">
                                                <button
                                                    className="btn ghost small"
                                                    onClick={() => navigate(`/plantillas/${plantilla.id}`)}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    className="btn ghost small danger"
                                                    onClick={() => handleEliminar(plantilla.id, plantilla.nombre)}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ));
                    })()
                )}
            </div>
        </div>

        {/* Modal fuera del .card para que el overlay (position: fixed) cubra
            toda la viewport y no quede limitado al stacking context del card. */}
        <PlantillaCategoriasModal
            open={showCategoriasModal}
            onClose={() => setShowCategoriasModal(false)}
            onChanged={cargarPlantillas}
        />
        </>
    );
};

export default PlantillasList;
